import {useEffect, useState} from 'react';
import {readAuthSafe} from '../utils/auth';

const unavailable = {summary: null, loading: false};
const loading = {summary: null, loading: true};
export const EDI_BATCH_SIZE = 200;

export default function useRoutingEdiSummary(identities, revision, apiBase, scopeHeaders, enabled = true) {
  const signature = JSON.stringify(identities);
  const headersSignature = JSON.stringify(scopeHeaders);
  const [state, setState] = useState(null);
  useEffect(() => {
    const rows = JSON.parse(signature);
    const ids = [...new Set(rows.map(row => row.id).filter(id => /^[1-9]\d*$/.test(String(id))))];
    const controller = new AbortController();
    let cancelled = false;
    const values = {};
    setState({revision, signature, headersSignature, values: {}});
    async function read() {
      for (let offset = 0; offset < ids.length; offset += EDI_BATCH_SIZE) {
        if (cancelled) return;
        const batch = ids.slice(offset, offset + EDI_BATCH_SIZE);
        try {
          const token = readAuthSafe()?.token;
          if (!token) throw new Error('Authentication required');
          const response = await fetch(`${apiBase}/routing-status/edi-summary?row_ids=${batch.join(',')}`, {
            headers: {...JSON.parse(headersSignature), Authorization: `Bearer ${token}`}, signal: controller.signal,
          });
          const payload = await response.json();
          if (!response.ok || payload?.ok !== true || !payload.summaries) throw new Error('Unavailable');
          for (const id of batch) {
            const summary = payload.summaries[String(id)];
            const expected = rows.filter(row => String(row.id) === String(id));
            const matches = summary?.identity && expected.every(row => Object.keys(row.identity).every(
              key => String(summary.identity[key] ?? '').trim() === String(row.identity[key] ?? '').trim()));
            values[id] = matches ? {summary, loading: false} : unavailable;
          }
        } catch (error) {
          if (cancelled) return;
          for (const id of batch) values[id] = unavailable;
        }
        if (!cancelled) setState({revision, signature, headersSignature, values: {...values}});
      }
    }
    if (enabled) void read();
    return () => { cancelled = true; controller.abort(); };
  }, [signature, revision, apiBase, headersSignature, enabled]);

  return id => {
    if (!enabled || !/^[1-9]\d*$/.test(String(id))) return unavailable;
    if (state?.revision !== revision || state?.signature !== signature || state?.headersSignature !== headersSignature) return loading;
    return state.values[id] || loading;
  };
}
